import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@caspeco/casper-ui-library.components.text";
import { AspectRatio, BreakPoint } from "types/ui";
import { useMinWidthMediaQuery } from "hooks/useMediaQuery";
import { ThemeColorVariable, ThemeSpaceVariable } from "@caspeco/casper-ui-library.base-ui.theme";
import { Tab, TabList } from "@caspeco/casper-ui-library.components.tabs";
import { Divider } from "@caspeco/casper-ui-library.components.divider";
import { Icon, Icons } from "@caspeco/casper-ui-library.components.icon";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { useMenuStore } from "store/menuStore";
import { sideNavigate } from "./SectionTabs.helpers";
import {
	StyledSectionTabs,
	StyledSectionTabsInnerWrapper,
	StyledSectionTabsOuterWrapper,
} from "./SectionTabs.styles";

function SectionTabs() {
	const { menu, selectedSection } = useMenuStore();
	const { t } = useTranslation();
	const navElement = useRef<null | HTMLElement>(null);
	const isLargerScreen = useMinWidthMediaQuery(BreakPoint.Small);
	const [visibleNavButtons, setVisibleNavButtons] = useState<{
		left: boolean;
		right: boolean;
	}>({ left: false, right: false });

	const onClickNavigateLeft = () => {
		sideNavigate(navElement, "left", 10, 450, 10);
	};

	const onClickNavigateRight = () => {
		sideNavigate(navElement, "right", 10, 450, 10);
	};

	useEffect(() => {
		const scrollableDiv = navElement.current;

		const isScrollable = () => {
			if (!scrollableDiv) {
				return false;
			}
			return scrollableDiv.scrollWidth > scrollableDiv.clientWidth;
		};

		const updateNavigationButtonVisibility = () => {
			if (!isScrollable() || !scrollableDiv) {
				setVisibleNavButtons({ left: false, right: false });
				return;
			}

			const maxScrollLeft = scrollableDiv.scrollWidth - scrollableDiv.clientWidth;

			const scrollLeft = Math.ceil(scrollableDiv.scrollLeft);

			if (scrollLeft === 0) {
				setVisibleNavButtons({ left: false, right: true });
			} else if (scrollLeft >= maxScrollLeft) {
				setVisibleNavButtons({ left: true, right: false });
			} else {
				setVisibleNavButtons({ left: true, right: true });
			}
		};

		window.addEventListener("resize", updateNavigationButtonVisibility);
		scrollableDiv?.addEventListener("scroll", updateNavigationButtonVisibility);

		updateNavigationButtonVisibility();

		return () => {
			window.removeEventListener("resize", updateNavigationButtonVisibility);
			scrollableDiv?.removeEventListener("scroll", updateNavigationButtonVisibility);
		};
	}, []);

	const sectionIsHidden = (parentDiv: HTMLElement, childDiv: HTMLElement): string | null => {
		const parentRect = parentDiv.getBoundingClientRect();
		const childRect = childDiv.getBoundingClientRect();

		if (parentRect.left > childRect.left) return "left";
		if (parentRect.right < childRect.right) return "right";
		return null;
	};

	const scrollVertically = useCallback(() => {
		// On smaller screens a different layout is used and the scrollIntoView
		// of the tab element will make this vertical scrolling redundant
		if (!isLargerScreen) return;
		// The menu content related to the tab might be overflowing,
		// scroll to the top of it by manually setting scrollTop to zero
		const menuElement = document.querySelector('section[id="menu"]');
		if (menuElement) {
			menuElement.scrollTop = 0;
		}
	}, [isLargerScreen]);

	const scrollHorizontally = useCallback(() => {
		if (!selectedSection) return;
		const selectedTabElement = document.getElementById(selectedSection.id);
		if (!selectedTabElement) return;

		// Scroll selected tab element into view
		// Use short timeout as a workaround for native issues with the scrollIntoView API
		setTimeout(() => {
			selectedTabElement.scrollIntoView({
				block: "end",
			});
		}, 100);

		if (!isLargerScreen) return;
		// On larger screens the overflow is hidden
		// and navigate left/right buttons might cover the tabElement
		// we need to additionaly check if we should scroll further horizontally
		const tabListElement = document.getElementById("sectiontabs");
		if (!tabListElement) return;

		const distance = 150;
		// If the selected section is hidden on the right side, scroll to the right
		if (sectionIsHidden(tabListElement, selectedTabElement) === "right") {
			sideNavigate(navElement, "right", 10, distance, 10);
		}
		// If the selected section is hidden on the left side, scroll to the left
		if (sectionIsHidden(tabListElement, selectedTabElement) === "left") {
			sideNavigate(navElement, "left", 10, distance, 10);
		}
	}, [isLargerScreen, selectedSection]);

	const scrollIntoView = useCallback(() => {
		scrollVertically();
		scrollHorizontally();
	}, [scrollVertically, scrollHorizontally]);

	useEffect(() => {
		// On change of selected tab (menu section), scroll relevant elements into view
		scrollIntoView();
	}, [scrollIntoView, selectedSection]);

	return (
		<StyledSectionTabsOuterWrapper>
			<StyledSectionTabsInnerWrapper>
				{visibleNavButtons.left && (
					<button
						id="sectiontabs-navigateleft"
						type="button"
						aria-label={t("action_navigate_left")}
						onClick={onClickNavigateLeft}
					>
						<Icon icon={Icons.ChevronLeft} color={ThemeColorVariable.OnBackground} />
					</button>
				)}
				<Text id="sectiontabs-label">{t("menu_sections")}</Text>
				<StyledSectionTabs aria-labelledby="sectiontabs-label" id="sectiontabs" ref={navElement}>
					<TabList gap={{ md: ThemeSpaceVariable.Small }}>
						{menu?.sections?.map((section) => {
							const { id, imageMap, name } = section;
							return (
								<span id={id} key={id}>
									<Tab
										imgSrc={imageMap.withSizeQuery(AspectRatio.OneByOne, "s")}
										imgAlt={t("misc_picture_of_x", { 0: name })}
										imgFallbackColor={ThemeColorVariable.BackgroundDisabled}
									>
										{name}
									</Tab>
								</span>
							);
						})}
					</TabList>
				</StyledSectionTabs>
				{visibleNavButtons.right && (
					<button
						type="button"
						id="sectiontabs-navigateright"
						aria-label={t("action_navigate_right")}
						onClick={onClickNavigateRight}
					>
						<Icon icon={Icons.ChevronRight} color={ThemeColorVariable.OnBackground} />
					</button>
				)}
			</StyledSectionTabsInnerWrapper>
			<Box mx={{ base: 0, sm: ThemeSpaceVariable.Medium }}>
				<Divider borderColor={ThemeColorVariable.OnBackgroundBorder} />
			</Box>
		</StyledSectionTabsOuterWrapper>
	);
}

export default SectionTabs;
